import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from "../components/Session";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import {
    FormInput,
    FormSwitch,
    FormTextArea
} from "../components/formComponents";
import { Button } from "@progress/kendo-react-buttons";
import { GetPermissions } from "../components/Options";
import '../StyleSheet.css';

const EditProductCategories = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const saveName = id ? "Update" : "Create";

    const [formKey, setFormKey] = React.useState(0);
    const [permissions, setPermissions] = React.useState(null);
    const [layout, setLayout] = React.useState(null);
    const [values, setValues] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    const getField = React.useCallback(
        (name) => layout?.find((f) => f.field === name),
        [layout]
    );

    // Initialize default values based on layout defaults once layout is loaded
    React.useEffect(() => {
        if (!layout) return;

        // If editing, values are set later via fetch
        if (!id) {
            setValues({
                name: getField("name")?.defaultValue || "",
                code: getField("code")?.defaultValue || "",
                description: getField("description")?.defaultValue || "",
                isMunicipalWaste: getField("isMunicipalWaste")?.defaultValue === "true",
                active: getField("active")?.defaultValue !== "false" // default true if not false
            });
        }
    }, [layout, id, getField]);

    // Redirect if user not logged in
    React.useEffect(() => {
        if (!localStorage.getItem("userid")) {
            navigate("/logout");
        }
    }, [navigate]);

    // Load permissions and layout, and if edit mode load values
    React.useEffect(() => {
        const loadData = async () => {
            try {
                // Load permissions
                await new Promise((resolve) =>
                    GetPermissions(
                        ["ViewProductCategory", "NewProductCategory", "EditProductCategory"],
                        (p) => {
                            setPermissions(p);
                            resolve();
                        }
                    )
                );

                // Load layout
                const init = {
                    method: "GET",
                    headers: { Authorization: "Bearer " + GetToken() }
                };

                const uiRes = await fetch(
                    configData.SERVER_URL +
                    "apiv7/userinterfaces?$filter=viewName eq 'EditProductCategories'",
                    init
                );
                let uiData = await uiRes.json();

                if (GetSession().IsMobile) {
                    uiData = uiData.map((x) => ({
                        ...x,
                        isVisible: x.isVisible && x.isVisibleOnMobile
                    }));
                }

                setLayout(uiData);

                // If editing, load existing values
                if (id) {
                    const catRes = await fetch(
                        `${configData.SERVER_URL}apiv7/productcategories/${id}`,
                        init
                    );
                    const catData = await catRes.json();

                    setValues({
                        name: catData.name,
                        code: catData.code,
                        description: catData.description,
                        isMunicipalWaste: catData.isMunicipalWaste,
                        active: catData.isActive
                    });

                    setFormKey((k) => k + 1);
                }
            } catch (error) {
                console.error("Error loading data:", error);
                // Optionally show error notification
            }
        };

        loadData();
    }, [id]);

    // Redirect based on permissions
    React.useEffect(() => {
        if (!permissions) return;

        if (!permissions.CanView) navigate("/error401");
        if (id && !permissions.CanEdit) navigate("/error401");
        if (!id && !permissions.CanCreate) navigate("/error401");
    }, [permissions, id, navigate]);

    const getDefaultData = (value, name) => {
        const fld = getField(name);
        if (!fld || fld.isVisible) return value;

        switch (fld.type) {
            case "numeric":
                return parseFloat(fld.defaultValue);
            case "boolean":
                return fld.defaultValue === "true";
            default:
                return fld.defaultValue;
        }
    };

    // Simple validation - only checking required fields here
    const validateForm = () => {
        if (!values) return false;
        if (!values.name?.trim()) return false;
        if (!values.code?.trim()) return false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            alert("Please fill in required fields.");
            return;
        }

        setSubmitting(true);

        try {
            const url = `${configData.SERVER_URL}apiv7/productcategories${id ? "/" + id : ""}`;

            await fetch(url, {
                method: id ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + GetToken()
                },
                body: JSON.stringify({
                    productCategoryId: id || 0,
                    name: getDefaultData(values.name, "name"),
                    code: getDefaultData(values.code, "code"),
                    description: getDefaultData(values.description, "description"),
                    isMunicipalWaste: getDefaultData(values.isMunicipalWaste, "isMunicipalWaste"),
                    isActive: getDefaultData(values.active, "active")
                })
            });

            navigate("/wastestreams", {
                state: { msg: id ? "Waste Stream updated" : "Waste Stream created" }
            });
        } catch (error) {
            console.error("Failed to save:", error);
            alert("Failed to save the product category.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!permissions || !layout || !values) {
        return <div style={{ padding: 20 }}>Loading...</div>;
    }

    return (
        <>
            <div className="background-3d" />
            <div className="form-wrapper">
                <Form
                    initialValues={values}
                    key={formKey}
                    render={() => (
                        <FormElement>
                            <div
                                style={{
                                    display: "grid",
                                    gap: 20,
                                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))"
                                }}
                            >
                                {getField("name")?.isVisible && (
                                    <Field
                                        name="name"
                                        label="Name"
                                        component={FormInput}
                                        validator={(v) => (!v ? "Required" : "")}
                                        onChange={(e) =>
                                            setValues((prev) => ({ ...prev, name: e.value }))
                                        }
                                    />
                                )}

                                {getField("code")?.isVisible && (
                                    <Field
                                        name="code"
                                        label="Code"
                                        component={FormInput}
                                        validator={(v) => (!v ? "Required" : "")}
                                        onChange={(e) =>
                                            setValues((prev) => ({ ...prev, code: e.value }))
                                        }
                                    />
                                )}

                                {getField("description")?.isVisible && (
                                    <div style={{ gridColumn: "1 / -1" }}>
                                        <Field
                                            name="description"
                                            label="Comment"
                                            component={FormTextArea}
                                            onChange={(e) =>
                                                setValues((prev) => ({ ...prev, description: e.value }))
                                            }
                                        />
                                    </div>
                                )}

                                {getField("active")?.isVisible && (
                                    <Field
                                        name="active"
                                        label="Active"
                                        component={FormSwitch}
                                        onChange={(e) =>
                                            setValues((prev) => ({ ...prev, active: e.value }))
                                        }
                                    />
                                )}

                                {getField("isMunicipalWaste")?.isVisible && (
                                    <Field
                                        name="isMunicipalWaste"
                                        label="Municipal Waste"
                                        component={FormSwitch}
                                        onChange={(e) =>
                                            setValues((prev) => ({ ...prev, isMunicipalWaste: e.value }))
                                        }
                                    />
                                )}
                            </div>

                            <div
                                style={{
                                    marginTop: 32,
                                    display: "flex",
                                    gap: 12,
                                    flexWrap: "wrap"
                                }}
                            >
                                <Button
                                    themeColor="primary"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {saveName}
                                </Button>

                                <Button onClick={() => navigate("/wastestreams")} disabled={submitting}>
                                    Back to List
                                </Button>
                            </div>
                        </FormElement>
                    )}
                />
            </div>
        </>
    );
};

export default EditProductCategories;
